import React, { useState } from "react";
import { Box, Card, CardContent, Alert } from "@mui/material";
import { useMonitoringData } from "./use-monitoring-data";
import useIncidentsFilter from "./use-incidents-filter";
import StatusPageHeader from "./status-page-header";
import FeedTabs from "./feed-tabs";
import StatusBanner from "./status-banner";
import MonitoringSummary from "./monitoring-summary";
import FilterBar from "./filter-bar";
import IncidentsSectionHeader from "./incidents-section-header";
import IncidentsTable from "./incidents-table";
import LoadingState from "./loading-state";

const StatusPage: React.FC = () => {
  const { data, isLoading, error, refresh } = useMonitoringData();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("oldest");

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  const currentFeed = data?.results?.[selectedFeed];
  const lastUpdated = currentFeed?.lastUpdated || new Date().toISOString();

  const filteredAndSortedIncidents = useIncidentsFilter({
    incidents: currentFeed?.incidents || [],
    searchQuery,
    statusFilter,
    severityFilter,
    sortBy,
  });

  return (
    <LoadingState
      isLoading={isLoading}
      error={error}
      hasData={!!data?.results?.length}
    >
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          minHeight: "calc(100vh - 140px)",
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid",
          borderColor: "grey.200",
          background: "linear-gradient(to bottom, #ffffff, #fafafa)",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <StatusPageHeader
            lastUpdated={lastUpdated}
            refreshing={refreshing}
            onRefresh={handleRefresh}
          />

          <FeedTabs
            feeds={data?.results || []}
            selectedFeed={selectedFeed}
            onFeedChange={setSelectedFeed}
          />

          <StatusBanner
            unresolvedIncidents={
              data?.results.flatMap((feed) =>
                feed.incidents.filter((incident) => !incident.isResolved),
              ) || []
            }
          />

          <MonitoringSummary feeds={data?.results || []} />

          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            severityFilter={severityFilter}
            onSeverityChange={setSeverityFilter}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />

          <IncidentsSectionHeader
            statusFilter={statusFilter}
            incidentCount={filteredAndSortedIncidents.length}
          />

          {filteredAndSortedIncidents.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {statusFilter === "active"
                ? "No active incidents - all systems operational!"
                : "No incidents match the current filters."}
            </Alert>
          ) : (
            <Box
              sx={{
                height: "calc(100vh - 680px)",
                minHeight: "400px",
                overflow: "hidden",
              }}
            >
              <IncidentsTable incidents={filteredAndSortedIncidents} />
            </Box>
          )}
        </CardContent>
      </Card>
    </LoadingState>
  );
};

export default StatusPage;
